"use server";

import { withTicketDefaults } from "@/lib/ticket-defaults";
import { randomUUID } from "node:crypto";
import { z } from "zod";
import { Prisma } from "@prisma/client";
import type { Severity, Environment, IssueType, ProjectPhase, CustomerVisibility, BusinessImpactLevel, TicketStatus, TicketOrigin, ProductArea, ResolutionType } from "@prisma/client";
import { SUPPORT_TICKET_INCLUDE } from "@/lib/index-includes";
import { defineAction } from "@/lib/define-action";
import { bestEffort, zodErrors } from "@/lib/errors";
import { prisma } from "@/lib/prisma";
import { authorizeDeletion } from "@/lib/deletion-requests";
import { can } from "@/lib/access-rules";
import { indexSupportTicket, removeSupportTicket, findSimilarTickets, type SimilarTicket } from "@/lib/search";
import { assetReferenceSchema } from "@/lib/sharepoint/assets";
import { createEscalation, type CreateEscalationResult } from "@/lib/escalations/lifecycle";
import { emitEscalationEvent } from "@/lib/escalation-events";
import { notifyTicketDuplicate, notifyTicketPromoted, notifyTicketResolved } from "@/lib/escalation-notifications";
import type { ArticleDraft } from "@/lib/article-types";
import { TICKET_STATUS_VALUES, TICKET_ORIGIN_VALUES, PRODUCT_AREA_VALUES, ENVIRONMENT_VALUES, RESOLUTION_TYPE_VALUES } from "./ticket-options";
import { recordTeamActivity } from "@/lib/team-activity";

// Support ticket server actions — its own file,
// same precedent as src/lib/escalation-actions.ts's own header comment:

// Re-fetches with the relations toSupportTicketDocument (src/lib/search.ts)
async function reindexTicket(id: number) {
  const ticket = await prisma.supportTicket.findUnique({
    where: { id },
    include: SUPPORT_TICKET_INCLUDE,
  });
  if (!ticket) return;
  await indexSupportTicket(ticket);
}

const OPTIONAL_TICKET_FIELDS = [
  "vendorCaseId",
  "origin",
  "clientId",
  "appId",
  "environment",
  "browser",
  "errorStrings",
  "reproSteps",
  "rootCause",
  "resolution",
  "resolutionType",
  "resolvedAt",
  "vendorAgent",
  "reportedBy",
  "participants",
  "tags",
] as const;

const createTicketSchema = z.object({
  vendorCaseId: z.string().trim().optional(),
  title: z.string().trim().min(1, "Title is required"),
  status: z.enum(TICKET_STATUS_VALUES).optional(),
  origin: z.enum(TICKET_ORIGIN_VALUES).optional(),
  clientId: z.number().optional(),
  appId: z.number().optional(),
  productArea: z.enum(PRODUCT_AREA_VALUES).optional(),
  environment: z.enum(ENVIRONMENT_VALUES).optional(),
  browser: z.string().trim().optional(),
  symptom: z.string().trim().optional(),
  // Array in, joined to one newline-per-message Text blob on save —
  // matches SupportTicket.errorStrings' own column type (String?
  errorStrings: z.array(z.string()).optional(),
  reproSteps: z.string().trim().optional(),
  rootCause: z.string().trim().optional(),
  resolution: z.string().trim().optional(),
  resolutionType: z.enum(RESOLUTION_TYPE_VALUES).optional(),
  isKnownLimitation: z.boolean().optional(),
  openedAt: z.coerce.date().optional(),
  resolvedAt: z.coerce.date().optional(),
  vendorAgent: z.string().trim().optional(),
  reportedBy: z.string().trim().optional(),
  participants: z.string().trim().optional(),
  tags: z.array(z.string().trim().min(1)).optional(),
  isManualEntry: z.boolean().optional(),
  assets: z.array(assetReferenceSchema).optional(),
});

export type CreateTicketResult =
  | { ok: true; id: number; reference: string; missingOptionalFields: string[] }
  | { ok: false; errors: Record<string, string> };

export const createTicketFromExtraction = defineAction({
  guard: "tickets:create",
  run: async ({ userId }, input: unknown): Promise<CreateTicketResult> => {
    const parsed = createTicketSchema.safeParse(input);
    if (!parsed.success) return { ok: false, errors: zodErrors(parsed.error) };
    const data = withTicketDefaults(parsed.data, new Date());
    const errorStringsText = data.errorStrings && data.errorStrings.length > 0 ? data.errorStrings.join("\n") : undefined;

    const ticket = await prisma.$transaction(async (tx) => {
      const created = await tx.supportTicket.create({
        data: {
          reference: `PENDING-${randomUUID()}`,
          vendorCaseId: data.vendorCaseId || null,
          title: data.title,
          status: data.status as TicketStatus,
          origin: (data.origin ?? "INTERNAL") as TicketOrigin,
          clientId: data.clientId ?? null,
          appId: data.appId ?? null,
          productArea: data.productArea as ProductArea,
          environment: (data.environment ?? null) as Environment,
          browser: data.browser || null,
          symptom: data.symptom,
          errorStrings: errorStringsText ?? null,
          reproSteps: data.reproSteps || null,
          rootCause: data.rootCause || null,
          resolution: data.resolution || null,
          resolutionType: (data.resolutionType ?? null) as ResolutionType,
          isKnownLimitation: data.isKnownLimitation ?? false,
          openedAt: data.openedAt,
          resolvedAt: data.resolvedAt ?? null,
          vendorAgent: data.vendorAgent || null,
          reportedBy: data.reportedBy || null,
          participants: data.participants || null,
          isManualEntry: data.isManualEntry ?? false,
          assets: data.assets && data.assets.length > 0 ? (data.assets as Prisma.InputJsonValue) : Prisma.JsonNull,
          creator: userId,
          tags: data.tags?.length
            ? {
                connectOrCreate: data.tags.map((name) => ({
                  where: { name },
                  create: { name, creator: userId },
                })),
              }
            : undefined,
        },
      });
      const reference = `TKT-${String(created.id).padStart(4, "0")}`;
      return tx.supportTicket.update({ where: { id: created.id }, data: { reference } });
    });

    const missingOptionalFields = OPTIONAL_TICKET_FIELDS.filter((field) => {
      const value = (data as Record<string, unknown>)[field];
      return value === undefined || value === null || (Array.isArray(value) && value.length === 0);
    });

    await bestEffort(recordTeamActivity({ kind: "ticket", actorId: userId, label: `${ticket.reference} ${ticket.title}`, link: `/support-tickets/${ticket.id}` }), "record team activity");
    return { ok: true, id: ticket.id, reference: ticket.reference, missingOptionalFields };
  },
  index: (o) => reindexTicket(o.id),
  paths: ["/support-tickets"],
});

const SCALAR_TICKET_FIELDS = [
  "vendorCaseId", "title", "status", "origin", "clientId", "appId", "productArea", "environment", "browser", "symptom",
  "reproSteps", "rootCause", "resolution", "resolutionType", "isKnownLimitation", "openedAt", "resolvedAt",
  "vendorAgent", "reportedBy", "participants",
] as const;

const updateTicketSchema = z.object({
  vendorCaseId: z.string().trim().optional(),
  title: z.string().trim().min(1, "Title is required").optional(),
  status: z.enum(TICKET_STATUS_VALUES).optional(),
  origin: z.enum(TICKET_ORIGIN_VALUES).optional(),
  clientId: z.number().nullable().optional(),
  appId: z.number().nullable().optional(),
  productArea: z.enum(PRODUCT_AREA_VALUES).optional(),
  environment: z.enum(ENVIRONMENT_VALUES).nullable().optional(),
  browser: z.string().trim().optional(),
  symptom: z.string().trim().min(1, "Symptom is required").optional(),
  errorStrings: z.array(z.string()).optional(),
  reproSteps: z.string().trim().optional(),
  rootCause: z.string().trim().optional(),
  resolution: z.string().trim().optional(),
  resolutionType: z.enum(RESOLUTION_TYPE_VALUES).nullable().optional(),
  isKnownLimitation: z.boolean().optional(),
  openedAt: z.coerce.date().optional(),
  resolvedAt: z.coerce.date().nullable().optional(),
  vendorAgent: z.string().trim().optional(),
  reportedBy: z.string().trim().optional(),
  participants: z.string().trim().optional(),
  tags: z.array(z.string().trim().min(1)).optional(),
  assets: z.array(assetReferenceSchema).optional(),
});

export type UpdateTicketResult = { ok: true } | { ok: false; errors: Record<string, string> };

export const updateTicket = defineAction({
  guard: "tickets:edit",
  run: async ({ userId }, id: number, input: unknown): Promise<UpdateTicketResult> => {
    const existing = await prisma.supportTicket.findUnique({ where: { id }, select: { id: true, status: true } });
    if (!existing) throw new Error("Support ticket not found");

    const parsed = updateTicketSchema.safeParse(input);
    if (!parsed.success) return { ok: false, errors: zodErrors(parsed.error) };
    const data = parsed.data;

    const update: Prisma.SupportTicketUpdateInput = {};
    for (const key of SCALAR_TICKET_FIELDS) if (data[key] !== undefined) (update as Record<string, unknown>)[key] = data[key];
    if (data.errorStrings !== undefined) update.errorStrings = data.errorStrings.join("\n") || null;

    if (data.tags !== undefined) {
      update.tags = {
        set: [],
        ...(data.tags.length > 0 && {
          connectOrCreate: data.tags.map((name) => ({
            where: { name },
            create: { name, creator: userId },
          })),
        }),
      };
    }

    if (data.assets !== undefined) {
      update.assets = data.assets.length > 0 ? (data.assets as Prisma.InputJsonValue) : Prisma.JsonNull;
    }

    if (Object.keys(update).length === 0) return { ok: true };

    const updated = await prisma.supportTicket.update({ where: { id }, data: update });
    await reindexTicket(id).catch((err) => console.error("Failed to reindex updated support ticket:", err));

    if (existing.status !== "RESOLVED" && updated.status === "RESOLVED") {
      await notifyTicketResolved({
        id: updated.id,
        reference: updated.reference,
        title: updated.title,
        creator: updated.creator,
      }).catch((err) => console.error("Failed to send ticket-resolved notification:", err));
    }

    return { ok: true };
  },
  paths: (_o, id: number) => ["/support-tickets", `/support-tickets/${id}`],
});

export const markTicketDuplicate = defineAction({
  guard: "tickets:edit",
  run: async (_c, id: number, ofId: number): Promise<{ ok: true } | { ok: false; error: string }> => {
    if (id === ofId) return { ok: false, error: "A ticket can't be a duplicate of itself." };

    const original = await prisma.supportTicket.findUnique({
      where: { id: ofId },
      select: { id: true, reference: true, title: true, creator: true },
    });
    if (!original) return { ok: false, error: "Original ticket not found." };

    const duplicateTicket = await prisma.supportTicket.update({
      where: { id },
      data: { status: "DUPLICATE", duplicateOfId: ofId },
    });
    await notifyTicketDuplicate({
      duplicateTicket: { id: duplicateTicket.id, reference: duplicateTicket.reference, title: duplicateTicket.title, creator: duplicateTicket.creator },
      originalTicket: { id: original.id, reference: original.reference, title: original.title, creator: original.creator },
    }).catch((err) => console.error("Failed to send duplicate-ticket notifications:", err));

    return { ok: true };
  },
  index: (_o, id: number) => reindexTicket(id),
  paths: (_o, id: number) => ["/support-tickets", `/support-tickets/${id}`],
});

export const checkSimilarTickets = defineAction({
  guard: "tickets:edit",
  run: async ({ access }, input: { symptom: string; errorStrings: string[] }): Promise<SimilarTicket[]> =>
    can(access, "tickets:view") ? findSimilarTickets(input) : [],
});

export type PromoteTicketSeverityInput = {
  ownerId: number;
  reporterName: string;
  reporterEmail?: string;
  environment: Environment;
  areaImpacted: string;
  issueType: IssueType;
  projectPhase: ProjectPhase;
  customerVisibility: CustomerVisibility;
  businessImpactLevel: BusinessImpactLevel;
  severity: Severity;
  recommendedSeverity: Severity;
  severityOverrideReason?: string;
};

export type PromoteTicketResult =
  | { ok: true; escalationId: number; reference: string }
  | { ok: false; errors: Record<string, string> };

export const promoteTicketToEscalation = defineAction({
  guard: "tickets:edit",
  run: async ({ userId }, ticketId: number, severityInput: PromoteTicketSeverityInput): Promise<PromoteTicketResult> => {
    const ticket = await prisma.supportTicket.findUnique({ where: { id: ticketId } });
    if (!ticket) throw new Error("Support ticket not found");
    if (ticket.escalationId !== null) {
      return { ok: false, errors: { form: "This ticket is already linked to an escalation." } };
    }
    if (ticket.clientId === null) {
      return { ok: false, errors: { form: "This ticket has no client set. An escalation always needs one. Set a client on the ticket first." } };
    }

    const result: CreateEscalationResult = await createEscalation({
      walkmeCaseId: ticket.vendorCaseId ?? undefined,
      clientId: ticket.clientId,
      appId: ticket.appId ?? undefined,
      reportedAt: ticket.openedAt,
      reporterName: severityInput.reporterName,
      reporterEmail: severityInput.reporterEmail,
      ownerId: severityInput.ownerId,
      severity: severityInput.severity,
      shortDescription: ticket.title,
      environment: severityInput.environment,
      areaImpacted: severityInput.areaImpacted,
      issueType: severityInput.issueType,
      projectPhase: severityInput.projectPhase,
      customerVisibility: severityInput.customerVisibility,
      businessImpactLevel: severityInput.businessImpactLevel,
      recommendedSeverity: severityInput.recommendedSeverity,
      severityOverrideReason: severityInput.severityOverrideReason,
    });
    if (!result.ok) return result;

    await prisma.supportTicket.update({ where: { id: ticketId }, data: { escalationId: result.id } });
    await emitEscalationEvent(prisma, {
      escalationId: result.id,
      type: "OTHER",
      message: `Promoted from support ticket ${ticket.reference}`,
      actorId: userId,
    });
    await notifyTicketPromoted({
      ticket: { id: ticket.id, reference: ticket.reference, creator: ticket.creator },
      escalationId: result.id,
      escalationReference: result.reference,
    }).catch((err) => console.error("Failed to send ticket-promoted notification:", err));
    return { ok: true, escalationId: result.id, reference: result.reference };
  },
  paths: (o, ticketId: number) => [`/support-tickets/${ticketId}`, `/escalations/${o.escalationId}`, "/escalations"],
});

export const linkTicketToEscalation = defineAction({
  guard: "tickets:edit",
  run: async ({ userId }, ticketId: number, escalationId: number): Promise<{ ok: true } | { ok: false; error: string }> => {
    const [ticket, escalation] = await Promise.all([
      prisma.supportTicket.findUnique({ where: { id: ticketId } }),
      prisma.escalation.findUnique({ where: { id: escalationId }, select: { id: true } }),
    ]);
    if (!ticket) return { ok: false, error: "Support ticket not found." };
    if (!escalation) return { ok: false, error: "Escalation not found." };
    if (ticket.escalationId !== null) return { ok: false, error: "This ticket is already linked to an escalation." };

    await prisma.supportTicket.update({ where: { id: ticketId }, data: { escalationId } });
    await emitEscalationEvent(prisma, { escalationId, type: "OTHER", message: `Linked support ticket ${ticket.reference}`, actorId: userId });
    return { ok: true };
  },
  paths: (_o, ticketId: number, escalationId: number) => [`/support-tickets/${ticketId}`, `/escalations/${escalationId}`],
});

export const unlinkTicketFromEscalation = defineAction({
  guard: "tickets:edit",
  run: async ({ userId }, ticketId: number): Promise<{ ok: true; escalationId: number | null }> => {
    const ticket = await prisma.supportTicket.findUnique({ where: { id: ticketId }, select: { escalationId: true, reference: true } });
    if (!ticket) throw new Error("Support ticket not found");
    const escalationId = ticket.escalationId;

    await prisma.supportTicket.update({ where: { id: ticketId }, data: { escalationId: null } });
    if (escalationId) {
      await emitEscalationEvent(prisma, { escalationId, type: "OTHER", message: `Unlinked support ticket ${ticket.reference}`, actorId: userId });
    }
    return { ok: true, escalationId };
  },
  paths: (o, ticketId: number) => [`/support-tickets/${ticketId}`, ...(o.escalationId ? [`/escalations/${o.escalationId}`] : [])],
});

export const searchEscalationsForLinking = defineAction({
  guard: "tickets:edit",
  run: async ({ access }, query: string) => {
    const q = query.trim();
    if (!q || !can(access, "escalations:view")) return [];
    return prisma.escalation.findMany({
      where: { deletedAt: null, OR: [{ reference: { contains: q } }, { shortDescription: { contains: q } }] },
      select: { id: true, reference: true, shortDescription: true },
      orderBy: { createdAt: "desc" },
      take: 10,
    });
  },
});

export const searchTicketsForLinking = defineAction({
  guard: "tickets:edit",
  run: async ({ access }, query: string) => {
    const q = query.trim();
    if (!q || !can(access, "tickets:view")) return [];
    return prisma.supportTicket.findMany({
      where: { deletedAt: null, escalationId: null, OR: [{ reference: { contains: q } }, { title: { contains: q } }] },
      select: { id: true, reference: true, title: true },
      orderBy: { createdAt: "desc" },
      take: 10,
    });
  },
});

export const createArticleFromTicket = defineAction({
  guard: "tickets:edit",
  run: async (_c, ticketId: number): Promise<{ ok: true; draft: ArticleDraft } | { ok: false; error: string }> => {
    const ticket = await prisma.supportTicket.findUnique({ where: { id: ticketId }, include: { tags: true } });
    if (!ticket) return { ok: false, error: "Support ticket not found." };
    if (ticket.status !== "RESOLVED" || !ticket.resolution?.trim()) {
      return { ok: false, error: "Only a resolved ticket with a recorded resolution can seed an article." };
    }
    return {
      ok: true,
      draft: {
        processName: ticket.title,
        type: "TECHNICAL",
        technicalKind: "GENERIC_FIX",
        fields: {
          context: ticket.symptom,
          prerequisites: [ticket.environment, ticket.browser].filter(Boolean).join(" · "),
          steps: ticket.resolution ?? "",
          knownIssues: ticket.isKnownLimitation ? "This is a known WalkMe limitation (not expected to be fixed by the vendor)." : undefined,
        },
        tagIds: ticket.tags.map((t) => t.id),
        clientId: ticket.clientId ?? undefined,
        appId: ticket.appId ?? undefined,
        sourceTicketIds: [ticket.id],
        sourceEscalationIds: [],
        sourceRef: ticket.reference,
      },
    };
  },
});

export const deleteTicket = defineAction({
  guard: (id: number, reason?: string) => authorizeDeletion("SUPPORT_TICKET", id, reason),
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- the guard reads the reason
  run: async (gate, id: number, _reason?: string): Promise<{ ok: true; requested?: true }> => {
    if ("requested" in gate) return { ok: true, requested: true };
    await prisma.supportTicket.update({ where: { id }, data: { deletedAt: new Date(), deletedBy: gate.userId } });
    return { ok: true };
  },
  index: (o, id: number) => !o.requested && removeSupportTicket(id),
  paths: ["/support-tickets"],
});

export const restoreTicket = defineAction({
  guard: "trash:restore",
  run: async (_c, id: number) => {
    await prisma.supportTicket.update({ where: { id }, data: { deletedAt: null, deletedBy: null } });
  },
  index: (_o, id: number) => reindexTicket(id),
  paths: ["/support-tickets", "/admin/trash"],
});
